import React, {
  createContext,
  useContext,
  useId,
  useMemo,
  useState,
  PropsWithChildren,
  KeyboardEvent,
} from "react";

/** tiny className joiner (avoids 'clsx' dep) */
function cx(...parts: Array<string | false | null | undefined>) {
  return parts.filter(Boolean).join(" ");
}

/**
 * Minimal, accessible Tabs (headless) with Tailwind-friendly classes.
 *
 * Usage:
 * <Tabs defaultValue="current">
 *   <TabsList>
 *     <TabsTrigger value="current">Current Model</TabsTrigger>
 *     <TabsTrigger value="ml">Machine Learning</TabsTrigger>
 *   </TabsList>
 *   <TabsContent value="current">…</TabsContent>
 *   <TabsContent value="ml">…</TabsContent>
 * </Tabs>
 */

type TabsCtx = {
  value: string;
  setValue: (v: string) => void;
  baseId: string;
  register: (val: string) => number; // for roving tabindex
  values: string[];
};

const TabsContext = createContext<TabsCtx | null>(null);
function useTabsCtx() {
  const ctx = useContext(TabsContext);
  if (!ctx) throw new Error("Tabs.* must be used within <Tabs>");
  return ctx;
}

type TabsProps = PropsWithChildren<{
  value?: string;
  defaultValue?: string;
  onValueChange?: (v: string) => void;
  className?: string;
}>;

export function Tabs({ value, defaultValue, onValueChange, className, children }: TabsProps) {
  const baseId = useId();
  const [uncontrolled, setUncontrolled] = useState<string>(defaultValue ?? "");
  const current = value ?? uncontrolled;

  const [values, setValues] = useState<string[]>([]);
  const register = (v: string) => {
    setValues((prev) => (prev.includes(v) ? prev : [...prev, v]));
    return values.indexOf(v);
  };

  const setValue = (v: string) => {
    if (value === undefined) setUncontrolled(v);
    onValueChange?.(v);
  };

  const _val = current || values[0] || ""; // ensure some value

  const ctx = useMemo(
    () => ({ value: _val, setValue, baseId, register, values }),
    [_val, baseId, values]
  );

  return (
    <TabsContext.Provider value={ctx}>
      <div className={cx("w-full", className)}>{children}</div>
    </TabsContext.Provider>
  );
}

type TabsListProps = PropsWithChildren<{ className?: string }>;
export function TabsList({ className, children }: TabsListProps) {
  const { values, value } = useTabsCtx();
  return (
    <div
      role="tablist"
      aria-orientation="horizontal"
      className={cx(
        "inline-flex items-center gap-1 rounded-lg bg-gray-100 p-1",
        "dark:bg-gray-900/40",
        className
      )}
      data-tabs-value={value}
      data-tabs-count={values.length}
    >
      {children}
    </div>
  );
}

type TabsTriggerProps = PropsWithChildren<{
  value: string;
  className?: string;
}>;

export function TabsTrigger({ value, className, children }: TabsTriggerProps) {
  const { value: active, setValue, baseId, register, values } = useTabsCtx();
  register(value);
  const selected = active === value;

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (!values.length) return;
    const currentIndex = values.indexOf(active);
    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      e.preventDefault();
      const next = values[(currentIndex + 1) % values.length];
      setValue(next);
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      e.preventDefault();
      const prev = values[(currentIndex - 1 + values.length) % values.length];
      setValue(prev);
    } else if (e.key === "Home") {
      e.preventDefault();
      setValue(values[0]);
    } else if (e.key === "End") {
      e.preventDefault();
      setValue(values[values.length - 1]);
    }
  };

  return (
    <button
      id={`${baseId}-tab-${value}`}
      role="tab"
      aria-selected={selected}
      aria-controls={`${baseId}-panel-${value}`}
      tabIndex={selected ? 0 : -1}
      onClick={() => setValue(value)}
      onKeyDown={onKeyDown}
      className={cx(
        "px-3 py-1.5 rounded-md text-sm font-medium transition",
        selected
          ? "bg-white text-gray-900 shadow-sm border border-gray-200"
          : "text-gray-600 hover:text-gray-900 hover:bg-white/70",
        "dark:text-gray-300 dark:hover:text-white dark:border-gray-700 dark:bg-gray-800",
        className
      )}
      data-state={selected ? "active" : "inactive"}
      data-value={value}
    >
      {children}
    </button>
  );
}

type TabsContentProps = PropsWithChildren<{ value: string; className?: string }>;
export function TabsContent({ value, className, children }: TabsContentProps) {
  const { value: active, baseId } = useTabsCtx();
  const hidden = active !== value;
  return (
    <div
      id={`${baseId}-panel-${value}`}
      role="tabpanel"
      aria-labelledby={`${baseId}-tab-${value}`}
      hidden={hidden}
      className={cx(hidden ? "hidden" : "block", className)}
      data-state={hidden ? "inactive" : "active"}
    >
      {!hidden && children}
    </div>
  );
}

export default Tabs;
